import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { compensationApi } from '../../../api/compensation.js';

const money = (v, c = 'IDR') => (v == null ? '—' : `${c} ${Number(v).toLocaleString('en-US')}`);

/**
 * The comp review worksheet: a manager grid of participant rows with proposed
 * amount/% and a LIVE budget-remaining indicator that updates after each save.
 * A draft cycle shows an Open action that snapshots participants from HRIS.
 */
export default function Worksheet() {
  const { id } = useParams();
  const [cycle, setCycle] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({}); // participantId → amount string

  const refresh = useCallback(() => {
    setLoading(true);
    compensationApi
      .getCycle(id)
      .then((d) => setCycle(d?.cycle || null))
      .catch(() => {});
    return compensationApi
      .listParticipants(id)
      .then((d) => {
        setCycle(d?.cycle || null);
        setParticipants(d?.participants || []);
        setBudget(d?.budget || null);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);

  async function openCycle() {
    setError(null);
    try {
      await compensationApi.openCycle(id);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open cycle');
    }
  }

  async function save(p) {
    setError(null);
    const raw = drafts[p.id];
    const proposedAmount = raw === undefined || raw === '' ? p.proposed_amount : Number(raw);
    try {
      await compensationApi.proposeParticipant(p.id, { proposedAmount });
      await refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Proposal failed');
    }
  }

  async function submit(p) {
    setError(null);
    try {
      await compensationApi.submitParticipant(p.id);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Submit failed');
    }
  }

  if (loading && !cycle) {
    return <div className="muted" data-testid="comp-worksheet-page" data-loading="true">Loading…</div>;
  }
  if (!cycle) return <div className="alert error">Cycle not found</div>;

  const currency = budget?.budgetCurrency || cycle.budget_currency || 'IDR';

  return (
    <section data-testid="comp-worksheet-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>{cycle.name}</h1>
          <p className="muted">
            <Link to="/compensation/cycles">← All cycles</Link> ·{' '}
            <span className={`chip status-${cycle.status}`} data-testid="comp-worksheet-cycle-status">{cycle.status}</span>
          </p>
        </div>
        <div className="actions">
          {cycle.status === 'draft' && (
            <button className="btn primary" data-testid="comp-cycle-open" onClick={openCycle}>Open cycle</button>
          )}
        </div>
      </header>

      {error && <div className="alert error" data-testid="comp-worksheet-error">{error}</div>}

      <div className="card kv-strip">
        <div>
          <div className="muted">Budget</div>
          <div>{money(budget?.budgetAmount, currency)}</div>
        </div>
        <div>
          <div className="muted">Allocated</div>
          <div>{money(budget?.allocated, currency)}</div>
        </div>
        <div>
          <div className="muted">Remaining</div>
          <div data-testid="comp-budget-remaining">{money(budget?.remaining, currency)}</div>
        </div>
      </div>

      <div className="card">
        <table className="data-table" data-testid="comp-worksheet-list">
          <thead>
            <tr>
              <th>Employee</th><th>Current Salary</th><th>Rating</th>
              <th>Proposed Amount</th><th>Proposed %</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.id} data-testid={`comp-worksheet-row-${p.employee_id}`}>
                <td>{p.employee_name || p.employee_code || p.employee_id}</td>
                <td>{money(p.current_salary, currency)}</td>
                <td>{p.rating || '—'}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    data-testid={`comp-worksheet-amount-${p.employee_id}`}
                    defaultValue={p.proposed_amount ?? ''}
                    disabled={p.status === 'approved' || p.status === 'submitted'}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                  />
                </td>
                <td>{p.proposed_pct != null ? `${Number(p.proposed_pct).toFixed(2)}%` : '—'}</td>
                <td><span className={`chip status-${p.status}`} data-testid={`comp-worksheet-status-${p.employee_id}`}>{p.status}</span></td>
                <td className="actions">
                  {p.status === 'pending' && (
                    <>
                      <button className="btn" data-testid={`comp-worksheet-save-${p.employee_id}`} onClick={() => save(p)}>Save</button>
                      <button className="btn primary" data-testid={`comp-worksheet-submit-${p.employee_id}`} onClick={() => submit(p)} disabled={p.proposed_amount == null}>Submit</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!loading && participants.length === 0 && (
              <tr><td colSpan={7} className="muted">No participants yet. Open the cycle to snapshot salaries from HRIS.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
